import React, { useContext } from 'react';
import styled, { css } from 'styled-components/macro';
import { definitionLabel } from 'styles/base-classes';
import ListLoader from 'components/utility/ContentLoader/ListLoader';
import { EntityContext } from 'context/entityContext';
import {
  ITopTerritoriesEntity,
  ITopTerritoriesProps,
  ITopTerritoriesEntityCountryItem,
  ITopTerritoriesEntityCityItem,
} from './types';

const TopTerritoriesWrap = styled.div<{
  compare: boolean;
  territoryType: string | undefined;
  marginTop: string | undefined;
}>`
  display: flex;
  flex-flow: column nowrap;
  ${({ marginTop }): string => (marginTop ? `margin-top: ${marginTop};` : '')}

  @media screen and (min-width: 100em) {
    padding-top: ${({ compare, territoryType }): string =>
      compare && territoryType === 'city' ? '1rem' : '0'};
  }
`;

const TopTerritoriesLabel = styled.h4<any>`
  ${definitionLabel};
  ${({ isMobileView, isStreaming }): string =>
    isMobileView | isStreaming
      ? `
    color: #001247;
    font-size: 16px;
    text-transform: none;
    font-weight: 600;
    letter-spacing: normal;
    margin-bottom: 9px;
  `
      : ''}
`;

const ListWrapper = styled.div<any>`
  display: flex;
  flex-flow: row nowrap;
  ${({ isFlexWrap }): any =>
    isFlexWrap &&
    css`
      @media screen and (min-width: 50rem) {
        flex-wrap: wrap;
      }
    `}

  ${({ isMobileView }): string =>
    isMobileView
      ? `
    margin-left: -0.2em;
  `
      : ''}
`;
const ListWrapperColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopTerritoriesTotalList = styled.ol`
  padding: 0;
  margin-right: 0.5rem;
  list-style: none;
  color: ${({ theme }): string => theme.NightBlue};
`;

const TopTerritoriesCityList = styled(TopTerritoriesTotalList)`
  padding-bottom: 1em;
`;

const TopTerritoriesList = styled(TopTerritoriesTotalList)`
  &:not(:first-of-type) {
    [data-rank]::before {
      content: '';
      min-width: 0;
    }
  }
`;

export const TopTerritoriesItem = styled<any>('li')`
  display: flex;
  align-items: center;
  color: ${({ dataColor }: any): string => dataColor};
  font-weight: ${({ fontWeight }: any): string => fontWeight};
  font-size: ${({ isMobile, isSmaller }: any): string =>
    isMobile && isSmaller ? '0.8175rem' : '1rem'};
  margin-bottom: ${({ isMobile, isSmaller }): string =>
    isMobile && isSmaller ? '0.3125rem' : '0'};

  ${({ showCursor }): string => showCursor && 'cursor: pointer;'}

  &::before {
    content: '#' attr(data-rank);
    display: inline-block;
    min-width: 1.2rem;
    margin-right: ${({ isMobile, isSmaller }): string =>
      isMobile && isSmaller ? '0.5rem' : '1rem'};
    color: ${({ isStreaming, theme }): string =>
      isStreaming ? '#8EA6BE' : theme.NightBlue};
    font-weight: ${({ fontWeight }: any): string => fontWeight};
    text-align: right;
  }
`;

const LoaderWrapper = styled.div`
  svg {
    width: 100%;
    max-width: 9.375em;
  }
`;

/**
 * TopTerritories displays a list of top territories for each entity.
 *
 * @param {object} props React props
 * @param {function} props.getKey Returns a string to use as a React key
 * @param {string} props.label Label to display above the list
 * @param {boolean} props.pending Is the fetch for this component pending?
 * @param {boolean} props.isMobileView Is mobile view currently presented?
 * @param {string} props.marginTop set margin top
 * @param {Array.<object>} props.topTerritories Array of top territory data for each entity
 * @param {object} props.TerritoryItem React Component to use to display the territory items
 */

const TopTerritories: React.FC<ITopTerritoriesProps> = (props) => {
  const {
    getKey,
    label,
    pending,
    topTerritories,
    TerritoryItem,
    type,
    isMobileView,
    marginTop,
    isSmaller,
    isStreaming,
    onClick,
    isFlexWrap,
  } = props;

  const ctx = useContext(EntityContext);
  const { compareMode } = ctx;
  const List =
    type === 'city' && compareMode
      ? TopTerritoriesCityList
      : TopTerritoriesList;
  const Wrapper = type === 'city' ? ListWrapperColumn : ListWrapper;

  return (
    <TopTerritoriesWrap
      compare={!!compareMode}
      territoryType={type}
      marginTop={marginTop}
      data-pw={`top-territory`}
    >
      <TopTerritoriesLabel
        isMobileView={isMobileView}
        isStreaming={isStreaming}
      >
        {label}
      </TopTerritoriesLabel>

      <Wrapper isMobileView={isMobileView} isStreaming isFlexWrap={isFlexWrap}>
        {pending && (
          <LoaderWrapper>
            <ListLoader
              len={5}
              itemWidth={150}
              itemHeight={16}
              itemMargin={8}
            />
          </LoaderWrapper>
        )}

        {topTerritories &&
          topTerritories.map(
            (entity: ITopTerritoriesEntity, index: number): JSX.Element => (
              <List key={`${entity.entity.id}${index}`}>
                {entity.data.map(
                  (
                    item:
                      | ITopTerritoriesEntityCountryItem
                      | ITopTerritoriesEntityCityItem,
                    itemIndex: number
                  ): JSX.Element => (
                    <TerritoryItem
                      compareMode={compareMode}
                      entity={entity.entity}
                      rank={item?.data[0].countryRank || itemIndex + 1}
                      item={item}
                      key={getKey(entity, item)}
                      isMobileView={isMobileView}
                      isSmaller={isSmaller}
                      isStreaming={isStreaming}
                      onClick={onClick}
                    />
                  )
                )}
              </List>
            )
          )}
      </Wrapper>
    </TopTerritoriesWrap>
  );
};
export default TopTerritories;
